import React, { useEffect, useState, useCallback  } from "react";
import { X, FileText, Users, Copy, Check, Search,Bell ,Pencil } from "lucide-react";
import sidebarImage from "../../../assests/SidebarImage.png";
import SidebarHeaderImage from "../../../assests/SidebarHeaderImage.png";
import { GetAssessmentResponders ,CreateRemainders, ExtendResponderDeadline  } from "../Services/CompanyServices";
import { sanitizeHtml, stripHtml, DESCRIPTION_RICH_CONTENT_STYLES } from "../../../Utility/sanitizeHtml";


const deadlineToInputValue = (deadline) => {
    if (!deadline) return "";
    const [datePart, timePart] = deadline.split(" ");
    if (!datePart) return "";
    const [day, month, year] = datePart.split("-");
    const [hh = "00", mm = "00"] = (timePart || "").split(":");
    if (!day || !month || !year) return "";
    return `${year}-${month}-${day}T${hh}:${mm}`;
};

const inputValueToDeadline = (value) => {
    if (!value) return "";
    const [datePart, timePart] = value.split("T");
    const [year, month, day] = datePart.split("-");
    const [hh = "00", mm = "00"] = (timePart || "").split(":");
    return `${day}-${month}-${year} ${hh}:${mm}:00`;
};


const inputValueToISODeadline = (value) => {
    if (!value) return "";
    const [datePart, timePart] = value.split("T");
    if (!datePart || !timePart) return "";
    const [hh = "00", mm = "00"] = timePart.split(":");
    return `${datePart}T${hh}:${mm}:00.000`;
};

const getStatusColor = (status) => {
    switch (status) {
        case "Published":
        case "Active":
            return "bg-green-100 text-green-700";
        case "Scheduled":
            return "bg-purple-100 text-purple-700";
        case "Completed":
            return "bg-blue-100 text-blue-700";
        case "Draft":
        case "Incomplete":
            return "bg-orange-100 text-orange-700";
        case "Inactive":
            return "bg-red-100 text-red-700";
        default:
            return "bg-gray-100 text-gray-700";
    }
};

const ResponderSkeleton = () => (
    <div className="rounded-xl border border-gray-100 bg-white p-3 animate-pulse">
        <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 shrink-0 rounded-full bg-gray-200" />
            <div className="flex-1 space-y-1.5">
                <div className="h-3 w-32 rounded bg-gray-200" />
                <div className="h-2.5 w-40 rounded bg-gray-100" />
            </div>
            <div className="h-5 w-24 shrink-0 rounded-full bg-gray-100" />
        </div>
        <div className="mt-2.5 h-9 w-full rounded-xl bg-gray-100" />
    </div>
);

const ResponderCard = ({ responder, selectable, isSelected, onToggle, onEdit }) => {
    const [copied, setCopied] = useState(false);
    const initial = responder.responderName?.trim()?.charAt(0)?.toUpperCase() || "?";

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(responder.assessmentUrl);
            setCopied(true);
            CustomToast("success", "Assessment link copied to clipboard.");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            CustomToast("fail", "Unable to copy the link.");
        }
    };
    const isLocked = selectable && (responder.isRespondCollected || responder.isDeadlineMet);
    return (
        <div className={`rounded-xl border bg-white p-3 shadow-sm transition ${isSelected ? "border-[#7f35b2] ring-1 ring-[#7f35b2]/30 bg-[#7f35b2]/5" : "border-gray-100"}${isLocked ? "opacity-60" : ""}`}>
            <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2.5">
                    {selectable && (
                        <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={responder.isRespondCollected || responder.isDeadlineMet}
                            onChange={() => onToggle(responder.responderUID)}
                            title={
                                responder.isRespondCollected
                                    ? "Already responded"
                                    : responder.isDeadlineMet
                                    ? "Deadline has been met"
                                    : ""
                            }
                            className="h-4 w-4 shrink-0 rounded border-gray-300 text-[#7f35b2] focus:ring-[#7f35b2] cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                        />
                    )}
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 border-[#7f35b2] text-sm font-semibold text-[#7f35b2] select-none">
                        {initial}
                    </span>
                    <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">{responder.responderName}</p>
                        <p className="truncate text-xs text-gray-400">{responder.responderMail}</p>
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-1.5">
                    <span
                        className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-semibold ${
                            responder.isRespondCollected
                                ? "bg-green-100 text-green-700"
                                : responder.isDeadlineMet
                                ? "bg-red-100 text-red-700"
                                : "bg-gray-100 text-gray-700"
                        }`}
                    >
                        <span className="h-1.5 w-1.5 rounded-full bg-current" />
                        {responder.isRespondCollected
                            ? "Completed"
                            : responder.isDeadlineMet
                            ? "Expired"
                            : "Not Completed"}
                    </span>

                    <span className="whitespace-nowrap rounded-full bg-[#7f35b2]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-[#7f35b2]">
                        {responder.channel} . {responder.experienceType}
                    </span>
                    
                </div>
            </div>

            <div className="mt-2.5 flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2.5">
                <a href={responder.assessmentUrl} rel="noopener noreferrer" target="_blank" className="min-w-0 flex-1 truncate text-xs text-blue-500 pointer-events-auto">{responder.assessmentUrl}</a>
                <button
                    type="button"
                    onClick={handleCopy}
                    className="flex shrink-0 items-center gap-1 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-600 transition hover:border-[#7f35b2] hover:text-[#7f35b2]"
                >
                    {copied ? <Check size={13} /> : <Copy size={13} />}
                    {copied ? "Copied" : "Copy"}
                </button>
                <button
                    type="button"
                    onClick={() => onEdit?.(responder)}
                    title="Edit responder"
                    className="flex shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white p-1.5 text-gray-600 transition hover:border-[#7f35b2] hover:text-[#7f35b2]"
                >
                    <Pencil size={13} />
                </button>
            </div>
        </div>
    );
};

const EditResponderDeadlinePanel = ({ responder, assessmentUID, onCancel, onSaved }) => {
    const [deadlineValue, setDeadlineValue] = useState(deadlineToInputValue(responder?.deadline));
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!deadlineValue) {
            CustomToast("fail", "Please select a deadline.");
            return;
        }
        if (!assessmentUID || !responder?.responderUID) {
            CustomToast("fail", "Missing assessment or responder reference.");
            return;
        }
        const formattedDeadline = inputValueToDeadline(deadlineValue);
        const isoDeadline = inputValueToISODeadline(deadlineValue);

        setSaving(true);
        try {
            const response = await ExtendResponderDeadline({
                assessmentUID,
                responderUID: responder.responderUID,
                newDeadline: isoDeadline,
            });

            if (response.data.status?.toLowerCase() === "success") {
                CustomToast("success", response.data.message || "Deadline updated successfully.");
                onSaved();
            } else {
                CustomToast("fail", response.data.message || "Unable to update deadline.");
            }
        } catch (error) {
            CustomToast("fail", error?.response?.data?.message || "Unable to update deadline.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="relative flex-1 min-h-0 flex flex-col px-3.5 pt-3.5 pb-0">
            <div className="mb-3 flex shrink-0 items-center gap-2">
                <button
                    onClick={onCancel}
                    className="flex items-center justify-center rounded-lg border border-gray-200 bg-white p-1.5 text-gray-600 hover:border-[#7f35b2] hover:text-[#7f35b2] transition"
                    title="Back to responders"
                >
                    <X size={14} />
                </button>
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
                    <Pencil size={13} className="text-[#7f35b2]" />
                    Edit Responder Deadline
                </div>
            </div>

            <div className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm space-y-3.5">
                <div>
                    <label className="text-xs text-gray-500">Responder Name</label>
                    <p className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-gray-50">
                        {responder.responderName}
                    </p>
                </div>

                <div>
                    <label className="text-xs text-gray-500">Deadline</label>
                    <input
                        type="datetime-local"
                        value={deadlineValue}
                        onChange={(e) => setDeadlineValue(e.target.value)}
                        className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#7f35b2]"
                    />
                </div>

                <div className="flex items-center justify-end gap-2.5 pt-1">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 rounded-lg text-sm text-white bg-[#7f35b2] hover:bg-[#6b2b96] transition disabled:opacity-60"
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};



const ViewAssessmentModal = ({ isOpen, assessment, onClose }) => {
    const [displayAssessment, setDisplayAssessment] = useState(assessment);
    const [responders, setResponders] = useState([]);
    const [respondersLoading, setRespondersLoading] = useState(false);
    const [responderSearch, setResponderSearch] = useState("");
    const [reminderMode, setReminderMode] = useState(false);
    const [selectedResponderUIDs, setSelectedResponderUIDs] = useState([]);
    const [showResponders, setShowResponders] = useState(false);
    const [editingResponder, setEditingResponder] = useState(null);

    useEffect(() => {
        if (assessment) setDisplayAssessment(assessment);
    }, [assessment]);

    useEffect(() => {
        document.body.style.overflow = isOpen ? "hidden" : "auto";
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isOpen]);

    const fetchResponders = useCallback(() => {
        if (!assessment?.uniqueKey) {
            setResponders([]);
            return Promise.resolve();
        }
        setRespondersLoading(true);
        return GetAssessmentResponders(assessment.uniqueKey)
            .then((res) => {
                if (res.data.status?.toLowerCase() === "success") {
                    setResponders(res.data.responders || []);
                } else {
                    setResponders([]);
                }
            })
            .catch(() => {
                setResponders([]);
            })
            .finally(() => {
                setRespondersLoading(false);
            });
    }, [assessment?.uniqueKey]);

    useEffect(() => {
        let isMounted = true;
        fetchResponders();
        return () => {
            isMounted = false;
        };
    }, [fetchResponders]);

    useEffect(() => {
        setResponderSearch("");
        setReminderMode(false);
        setSelectedResponderUIDs([]);
        setShowResponders(false);
        setEditingResponder(null);
    }, [assessment?.uniqueKey]);

    const filteredResponders = responders.filter((r) => {
        const q = responderSearch.trim().toLowerCase();
        if (!q) return true;
        return (
            r.responderName?.toLowerCase().includes(q) ||
            r.responderMail?.toLowerCase().includes(q) ||
            r.channel?.toLowerCase().includes(q) ||
            r.experienceType?.toLowerCase().includes(q)
        );
    });

    if (!displayAssessment) return null;

    const toggleResponderSelection = (responderUID) => {
        setSelectedResponderUIDs((prev) =>
            prev.includes(responderUID)
                ? prev.filter((id) => id !== responderUID)
                : [...prev, responderUID]
        );
    };

    const handleSendReminder = async () => {
        if (selectedResponderUIDs.length === 0) return;

        try {
            const response = await CreateRemainders({
                assessmentUID: assessment?.uniqueKey,
                responderUID: selectedResponderUIDs,
            });

            if (response.data.status?.toLowerCase() === "success") {
                CustomToast("success", response.data.message || "Reminder sent successfully.");
            } else {
                CustomToast("fail", response.data.message || "Unable to send reminder.");
                return;
            }
        } catch (error) {
            CustomToast("fail", error.response?.data?.message || "Unable to send reminder.");
            return;
        }

        setReminderMode(false);
        setSelectedResponderUIDs([]);
    };

    const canSendReminder =
        (displayAssessment?.status === "Active" || displayAssessment?.status === "Scheduled") &&
        responders.length > 0;

    const handleResponderSaved = () => {
        setEditingResponder(null);
        fetchResponders();
    };

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                className={`fixed inset-0 bg-slate-900/40 backdrop-blur-[2px]
                        transition-opacity duration-300 z-[9998]
                        ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
            />

            {/* Drawer */}
            <div
                className={`fixed top-0 right-0 h-full w-full md:w-[640px]
                    bg-white shadow-2xl z-[9999]
                    rounded-l-2xl overflow-hidden
                    transform transition-transform duration-300 ease-in-out
                    flex flex-col
                    ${isOpen ? "translate-x-0" : "translate-x-full"}`}
            >
                <div className="relative overflow-hidden flex items-center justify-between px-6 py-5 border-b bg-green-50 shrink-0">
                    <div
                        className="absolute inset-0 bg-no-repeat bg-cover bg-center pointer-events-none"
                        style={{ backgroundImage: `url(${SidebarHeaderImage})` }}
                    />
                    <div className="absolute inset-0 bg-black/35 pointer-events-none" />
                    <div className="relative z-10 flex items-center justify-between w-full">
                        <div className="flex items-center gap-3">
                            <div className="bg-[#7f35b2]/30 p-2.5 rounded-xl shrink-0">
                                <FileText className="text-white" size={22} />
                            </div>
                            <div>
                                <h2 className="text-2xl font-semibold text-gray-100">Assessment Details</h2>
                                <p className="text-sm text-gray-100 mt-0.5">View Assessment information</p>
                            </div>
                        </div>

                         <div className="flex items-center gap-2">
                            {responders.length > 0 && (
                                <button
                                    onClick={() => setShowResponders((prev) => !prev)}
                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold transition ${showResponders
                                        ? "bg-white/90 text-[#7f35b2] hover:bg-white"
                                        : "bg-white/20 text-gray-100 hover:bg-white/30"
                                        }`}
                                >
                                    <Users size={14} />
                                    {showResponders ? "Hide Responders" : "View Responder"}
                                </button>
                            )}
                            
                        </div>
                    </div>
                </div>
                <div className="relative flex-1 min-h-0 overflow-hidden flex flex-col">
                    <div
                        className="absolute inset-0 bg-no-repeat bg-right-bottom opacity-[0.07] pointer-events-none"
                        style={{
                            backgroundImage: `url(${sidebarImage})`,
                            backgroundSize: "360px auto",
                        }}
                    />

                    {!showResponders && (
                        <div className="relative shrink-0 p-3.5 pb-0 space-y-6">
                            <div>
                                <div className="flex gap-3">
                                    <p className="text-xl font-bold text-gray-900">{displayAssessment.surveyName}</p>
                                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(displayAssessment.status)}`}>
                                        {displayAssessment.status === "Incomplete" ? "Draft" : displayAssessment.status}
                                        <span className="w-1.5 h-1.5 rounded-full bg-current" />
                                    </span>
                                </div>
                                <style>{DESCRIPTION_RICH_CONTENT_STYLES}</style>
                                <div
                                    className="description-rich-content text-gray-500 text-sm mt-1 line-clamp-5"
                                    title={stripHtml(displayAssessment.surveyDescription)}
                                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(displayAssessment.surveyDescription) }}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 rounded-xl p-3">
                                    <p className="text-xs text-gray-400">Questions</p>
                                    <p className="text-lg font-semibold text-gray-900">{displayAssessment.totalQuestions}</p>
                                </div>
                                <div className="bg-gray-50 rounded-xl p-3">
                                    <p className="text-xs text-gray-400">Responses</p>
                                    <p className="text-lg font-semibold text-gray-900">{displayAssessment.responses ?? 0}</p>
                                </div>
                            </div>
                        </div>
                    )}
                    {showResponders && editingResponder && (
                        <EditResponderDeadlinePanel
                            responder={editingResponder}
                            assessmentUID={assessment?.uniqueKey}
                            onCancel={() => setEditingResponder(null)}
                            onSaved={handleResponderSaved}
                        />
                    )}
                    {showResponders && !editingResponder && (
                        <div className="relative flex-1 min-h-0 flex flex-col px-3.5 pt-3.5 pb-0">
                        <div className="mb-3 flex shrink-0 items-center justify-between gap-2">
                        
                            <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                <Users size={13} className="text-[#7f35b2]" />
                                Responders {responders.length > 0 && `(${responders.length})`}
                            </div>

                            <div className="flex items-center gap-2">
                                {responders.length > 0 && (
                                    <div className="relative">
                                        <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={responderSearch}
                                            onChange={(e) => setResponderSearch(e.target.value)}
                                            placeholder="Search"
                                            className="w-72 rounded-full border border-gray-200 bg-gray-50 py-1 pl-7 pr-2.5 text-xs text-[#7f35b2] outline-none transition focus:w-66 focus:border-[#7f35b2] focus:bg-white focus:ring-1 focus:ring-[#7f35b2]/30"
                                        />
                                    </div>
                                )}

                                
                            </div>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto space-y-2.5 pr-1">
                            {respondersLoading &&
                                Array.from({ length: 3 }).map((_, i) => <ResponderSkeleton key={i} />)}

                            {!respondersLoading && responders.length === 0 && (
                                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-6 text-center">
                                    <p className="text-sm text-gray-400">No responders added yet.</p>
                                </div>
                            )}

                            {!respondersLoading && responders.length > 0 && filteredResponders.length === 0 && (
                                <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 py-6 text-center">
                                    <p className="text-sm text-gray-400">No responders match "{responderSearch}".</p>
                                </div>
                            )}

                            {!respondersLoading &&
                                filteredResponders.map((responder) => (
                                    <ResponderCard key={responder.responderUID} responder={responder} selectable={reminderMode}
                                        isSelected={selectedResponderUIDs.includes(responder.responderUID)}
                                        onToggle={toggleResponderSelection}
                                        onEdit={setEditingResponder}/>
                                ))}
                        </div>
                    </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t bg-white px-6 py-4 flex items-center justify-between gap-3 shrink-0">
                    <div>
                        {showResponders && !editingResponder && canSendReminder && (
                            <button
                                type="button"
                                onClick={() => {
                                    setReminderMode((prev) => !prev);
                                    setSelectedResponderUIDs([]);
                                }}
                                className={`flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold transition ${
                                    reminderMode
                                        ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                        : "bg-[#7f35b2]/10 text-[#7f35b2] hover:bg-[#7f35b2]/20"
                                }`}
                            >
                                {!reminderMode && <Bell size={16} />}
                                {reminderMode ? "Cancel" : "Reminder"}
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        {reminderMode && (
                            <button
                                onClick={handleSendReminder}
                                disabled={selectedResponderUIDs.length === 0}
                                className="px-5 py-2.5 rounded-xl bg-[#7f35b2] text-white font-medium hover:bg-[#6b2b96] transition disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                Send Reminder {selectedResponderUIDs.length > 0 && `(${selectedResponderUIDs.length})`}
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className="px-5 py-2.5 border border-gray-300 rounded-xl hover:bg-gray-50 transition"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>

        </>
    );
};

export default ViewAssessmentModal;

